import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../components/Tabella/ChipFilter';
import { CampoRicerca } from '../../../components/Util/CampoRicerca';
import { sportLabel } from '../../../components/Util/sportsCatalog';
import {
    CERTIFICATI_SIMULAZIONE,
    STATI_CERTIFICATO,
    elencoCertificati,
    etichettaStatoCertificato,
    caricaCertificato,
    richiediCertificato,
} from '../../../../api-client/certificatiMedici';

/*
 * Certificati medici dei tesserati (menu sotto Anagrafiche). La riga apre la scheda del tesserato
 * (scheda.js). «Nuovo certificato» apre una finestra: si chiede il certificato al tesserato (email e notifica,
 * lui accetta o rifiuta dal web player) oppure lo si carica a mano (file e scadenza, vale subito).
 */

export const LIST_URL = '/struttura/certificati';
const STATO_OPTIONS = ['Da verificare', 'Richiesto', 'Valido', 'Scaduto', 'Non approvato', 'Rifiutato'];

export const dataIt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
export const coloreStato = (s) => ({ VALID: 'text-success', REQUESTED: 'text-warning', TO_VERIFY: 'text-warning', EXPIRED: 'text-danger', REFUSED: 'text-danger', NOT_APPROVED: 'text-danger' }[s] || 'text-muted');

/**
 * Finestra «Nuovo certificato» (anche dalla scheda, con il tesserato già scelto). Due modi:
 * - «Carica il certificato» (la prima): la struttura allega il file (PDF o foto) con la scadenza e il certificato vale subito;
 * - «Chiedi al tesserato»: email e notifica al player, che accetta o rifiuta dal web player.
 * `onInviata(tesserato, modo)`.
 */
export const FinestraRichiesta = ({ show, onHide, tesserati, personId: scelto = '', modoIniziale = 'carica', onInviata }) => {
    const [modo, setModo] = useState(modoIniziale);
    const [personId, setPersonId] = useState(scelto);
    const [nota, setNota] = useState('');
    const [file, setFile] = useState(null);
    const [scadenza, setScadenza] = useState('');
    const [errore, setErrore] = useState('');
    const [invio, setInvio] = useState(false);
    useEffect(() => {
        if (show) { setModo(modoIniziale); setPersonId(scelto); setNota(''); setFile(null); setScadenza(''); setErrore(''); }
    }, [show, scelto, modoIniziale]);

    const opzioni = useMemo(() => (tesserati || []).map((t) => ({
        value: t.personId,
        label: t.name,
        dettaglio: `${t.personType === 'JUNIOR' ? 'Junior' : 'Player'} · ${sportLabel(t.sportType)} · ${etichettaStatoCertificato(t.certificateStatus)}`,
    })), [tesserati]);
    const t = (tesserati || []).find((x) => x.personId === personId);
    const carica = modo === 'carica';
    // Certificato già approvato e valido oltre i prossimi 30 giorni: il server non accetta una nuova richiesta
    const giaValido = t?.certificateStatus === 'VALID' && t.expiration
        && new Date(`${String(t.expiration).slice(0, 10)}T00:00:00`) - new Date() > 30 * 86400000;

    const invia = async (e) => {
        if (e) e.preventDefault();
        if (!t) { setErrore('Scegli il tesserato.'); return; }
        setInvio(true);
        setErrore('');
        const r = carica
            ? await caricaCertificato({ personId: t.personId, personType: t.personType, sportType: t.sportType, expiration: scadenza, file })
            : await richiediCertificato({ personId: t.personId, personType: t.personType, sportType: t.sportType, message: nota });
        setInvio(false);
        if (!r || r.error) { setErrore(r?.error || (carica ? 'Certificato non caricato.' : 'Richiesta non inviata.')); return; }
        onInviata(t, modo);
    };

    return (
        <Modal show={show} onHide={() => !invio && onHide()} centered>
            <Form onSubmit={invia}>
                <Modal.Header closeButton><Modal.Title>Nuovo certificato</Modal.Title></Modal.Header>
                <Modal.Body>
                    <div className="sid-scelta-modo mb-4" role="radiogroup" aria-label="Come aggiungere il certificato">
                        <button type="button" role="radio" aria-checked={carica} className={carica ? 'is-attiva' : ''} onClick={() => setModo('carica')}>Carica il certificato</button>
                        <button type="button" role="radio" aria-checked={!carica} className={!carica ? 'is-attiva' : ''} onClick={() => setModo('richiedi')}>Chiedi al tesserato</button>
                    </div>

                    <Form.Label htmlFor="cert-tesserato">Tesserato</Form.Label>
                    <CampoRicerca id="cert-tesserato" opzioni={opzioni} valore={personId} onChange={setPersonId} nessunRisultato="Nessun tesserato con questo nome" />

                    {carica ? (
                        <>
                            <Form.Label htmlFor="cert-file" className="mt-3">File del certificato (PDF o foto, al massimo 3 MB)</Form.Label>
                            <Form.Control id="cert-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                            <Form.Label htmlFor="cert-scadenza" className="mt-3">Scadenza</Form.Label>
                            <Form.Control id="cert-scadenza" type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} style={{ maxWidth: 220 }} />
                            <p className="sid-nota-leggibile mt-3 mb-0">
                                Il certificato vale subito: Valido fino alla scadenza, poi Scaduto. {t?.personType === 'JUNIOR' ? 'Il genitore del junior' : 'Il player'} lo
                                trova tra i suoi certificati nel web player e riceve una notifica.
                            </p>
                        </>
                    ) : (
                        <>
                            {giaValido && (
                                <Alert variant="warning" className="mt-3 mb-0">
                                    Hai già il certificato di {t.name}, valido fino al {dataIt(t.expiration)}: lo puoi chiedere di nuovo
                                    nei 30 giorni prima della scadenza.
                                </Alert>
                            )}
                            <Form.Label htmlFor="cert-nota" className="mt-3">Nota per il player (facoltativa)</Form.Label>
                            <Form.Control id="cert-nota" as="textarea" rows={3} maxLength={500} value={nota} onChange={(e) => setNota(e.target.value)} />
                            <p className="sid-nota-leggibile mt-3 mb-0">
                                {t?.personType === 'JUNIOR'
                                    ? 'Il genitore del junior riceve un\'email e una notifica nell\'app SportID, e risponde dal web player. '
                                    : 'Il player riceve un\'email e una notifica nell\'app SportID, e risponde dal web player. '}
                                Quando accetta o rifiuta ti arriva un'email, con la sua motivazione se l'ha scritta.
                            </p>
                        </>
                    )}
                    {errore && <div className="alert alert-danger mt-3 mb-0">{errore}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn btn-light btn-add" onClick={onHide} disabled={invio}>Annulla</Button>
                    <Button type="submit" className="btn btn-primary btn-add" disabled={invio || !t}>
                        {invio ? (carica ? 'Caricamento…' : 'Invio…') : (carica ? 'Carica certificato' : 'Invia richiesta')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export const ListaCertificati = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [righe, setRighe] = useState(null);
    const [errore, setErrore] = useState('');
    const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);
    const [richiesta, setRichiesta] = useState(false);
    const [inviata, setInviata] = useState(location.state?.inviata || null);

    const carica = useCallback(async () => {
        const r = await elencoCertificati();
        if (!r || r.error) { setErrore(r?.error || 'Impossibile leggere i certificati.'); setRighe([]); return; }
        setRighe(r.map((x) => ({
            ...x,
            stato: etichettaStatoCertificato(x.certificateStatus),
            sport: sportLabel(x.sportType),
            tipo: x.personType === 'JUNIOR' ? 'Junior' : 'Player',
            aggiornato: [x.uploadedAt, x.request?.review?.at, x.request?.respondedAt, x.request?.createdAt].filter(Boolean).sort().pop() || '',
            scadenza: x.expiration || '',
        })));
    }, []);
    useEffect(() => { carica(); }, [carica]);

    const filtrate = useMemo(() => righe && righe.filter((r) => filtroMultiplo(statoFilter, STATO_OPTIONS, r.stato)), [righe, statoFilter]);

    const data_column = [
        { Header: 'Aggiornato', Footer: 'Aggiornato', accessor: 'aggiornato', compact: true, disableGlobalFilter: true, Cell: ({ value }) => dataIt(value) || '—' },
        { Header: 'Tesserato', Footer: 'Tesserato', accessor: 'name', larghezza: '28%' },
        { Header: 'Tipo', Footer: 'Tipo', accessor: 'tipo', compact: true },
        { Header: 'Sport', Footer: 'Sport', accessor: 'sport' },
        { Header: 'Scadenza', Footer: 'Scadenza', accessor: 'scadenza', compact: true, disableGlobalFilter: true, Cell: ({ value }) => dataIt(value) || '—' },
        // Lo stato è sempre l'ultima colonna, prima della freccia (regola SUI)
        {
            Header: 'Stato',
            Footer: 'Stato',
            accessor: 'stato',
            disableGlobalFilter: true,
            Cell: ({ row }) => (
                <div className="d-flex align-items-center">
                    <i className={`fa fa-circle me-1 ${coloreStato(row.original.certificateStatus)}`}></i>
                    <div>{row.original.stato}</div>
                </div>
            ),
        },
    ];

    return (
        <>
            <Modal show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>
            <FinestraRichiesta
                show={richiesta}
                onHide={() => setRichiesta(false)}
                tesserati={righe}
                onInviata={(t, modo) => { setRichiesta(false); setInviata({ nome: t.name, modo }); carica(); }}
            />
            {CERTIFICATI_SIMULAZIONE && (
                <Alert variant="warning" className="text-dark">
                    <strong>Simulazione:</strong> tesserati e richieste sono di prova e restano in questo browser. Nella scheda
                    di un tesserato con una richiesta in attesa puoi rispondere al posto del player.
                </Alert>
            )}
            {inviata && (
                <Alert variant="success" className="text-dark" dismissible onClose={() => setInviata(null)}>
                    {inviata.modo === 'carica'
                        ? <>Certificato di <strong>{inviata.nome}</strong> caricato: vale subito.</>
                        : <>Richiesta inviata a <strong>{inviata.nome}</strong>: riceve un'email e una notifica nell'app.</>}
                </Alert>
            )}
            <TabellaLista
                openColumn={'name'}
                openRow={(row) => navigate(`${LIST_URL}/${row.personId}`)}
                cardTitle={'Certificati medici'}
                pageList={true}
                hasSearch={true}
                searchPlaceholder={'Cerca per tesserato o sport'}
                hasFilter={false}
                filterControl={
                    <div className="sid-chip-filters">
                        <ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
                    </div>
                }
                hasAddMoreAsModal={true}
                modalCallBack={() => setRichiesta(true)}
                labelAddMore={'Nuovo certificato'}
                data_column={data_column}
                data_json={filtrate}
                sort_by={'aggiornato'}
                sort_type={'desc'}
            />
        </>
    );
};

export { STATI_CERTIFICATO };
export default ListaCertificati;
